/**
 * Axios 实例封装。
 *
 * - baseURL 从环境变量 VITE_API_BASE_URL 读取（默认 "/api"）
 * - 请求拦截器：自动注入 Authorization: Bearer {token}
 * - 响应拦截器：统一解包 {code, data, message}，code !== 0 抛出业务错误
 * - 401 状态码：清除 token 并跳转登录页
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

/** 后端统一响应体。 */
interface BackendResponse<T = unknown> {
  code: number;
  data: T;
  message: string;
}

/** 业务错误类（携带 code + message）。 */
export class ApiError extends Error {
  code: number;
  constructor(code: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}

/** 创建 Axios 实例。 */
const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * 请求拦截器：从 localStorage 读取 token 并注入 Authorization 头。
 * 注意：不能直接 import authStore（会导致循环依赖），从 localStorage 读取即可。
 */
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("geo_access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * 响应拦截器：统一处理 {code, data, message} 格式。
 * - code === 0：返回 response.data.data（已解包）
 * - code !== 0：抛出 ApiError
 * - HTTP 401：清除 token，跳转登录页
 *
 * 注意：拦截器返回解包后的 data（而非 AxiosResponse），调用方使用
 * client.get<unknown, T>() 泛型声明返回类型为 T。
 */
client.interceptors.response.use(
  (response) => {
    const body = response.data as BackendResponse;

    // 非 {code, data, message} 格式（如文件下载），直接返回原始数据
    if (body === null || typeof body !== "object" || body.code === undefined) {
      return response.data as unknown as never;
    }

    if (body.code !== 0) {
      // 业务错误
      throw new ApiError(body.code, body.message || "请求失败");
    }

    // 成功：返回解包后的 data（通过类型断言绕过 AxiosResponse 约束）
    return body.data as unknown as never;
  },
  (error: AxiosError<BackendResponse & { detail?: string | { msg: string }[] }>) => {
    if (error.response?.status === 401) {
      // 认证失败：清除 token，跳转登录
      localStorage.removeItem("geo_access_token");
      localStorage.removeItem("geo_refresh_token");
      localStorage.removeItem("geo_account_info");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(new ApiError(40100, "认证失败，请重新登录"));
    }

    // 后端返回了 {code, data, message} 格式的错误
    const body = error.response?.data;
    if (body && body.code !== undefined) {
      return Promise.reject(new ApiError(body.code, body.message || "请求失败"));
    }
    if (body?.detail) {
      const message = typeof body.detail === "string" ? body.detail : body.detail.map(x => x.msg).join("；");
      return Promise.reject(new ApiError(error.response?.status || 500, message));
    }

    // 网络错误或其他
    const msg = error.message || "网络错误";
    return Promise.reject(new ApiError(50000, msg));
  },
);

export default client;
